import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";
import { toast } from "sonner";
import { ScanLine } from "lucide-react";
import OcrImportModal from "./OcrImportModal";
import AbilityBoostWizardPf2e from "./AbilityBoostWizardPf2e";
import AbilityScoreWizardDnd5e from "./AbilityScoreWizardDnd5e";

const ABILITIES = ["str", "dex", "con", "int", "wis", "cha"];
const abilityMod = (score) => Math.floor((score - 10) / 2);

export default function CharacterCreatorModal({ open, onOpenChange, gameId, game, onCreated }) {
  const isHomebrew = game?.system === "homebrew";
  const isDnd5e = game?.system === "dnd5e";
  const ruleset = game?.homebrew_ruleset;
  const [mode, setMode] = useState("full");
  const [name, setName] = useState("");
  const [ancestry, setAncestry] = useState("");
  const [background, setBackground] = useState("");
  const [klass, setKlass] = useState("");
  const [customAncestryName, setCustomAncestryName] = useState("");
  const [customBackgroundName, setCustomBackgroundName] = useState("");
  const [customClassName, setCustomClassName] = useState("");
  const [level, setLevel] = useState(1);
  const [abilities, setAbilities] = useState({ str: 10, dex: 10, con: 10, int: 10, wis: 10, cha: 10 });
  const [ancestries, setAncestries] = useState([]);
  const [backgrounds, setBackgrounds] = useState([]);
  const [classes, setClasses] = useState([]);
  const [saving, setSaving] = useState(false);
  const [ocrOpen, setOcrOpen] = useState(false);
  const [ocrExtra, setOcrExtra] = useState(null);
  const [dnd, setDnd] = useState({ race: "", class_name: "", background: "", alignment: "" });
  const [pf2eWizardOpen, setPf2eWizardOpen] = useState(false);
  const [dnd5eWizardOpen, setDnd5eWizardOpen] = useState(false);
  const [boostHistory, setBoostHistory] = useState([]);
  const [hbAncestries, setHbAncestries] = useState([]);
  const [hbClasses, setHbClasses] = useState([]);
  const [hbAncestryId, setHbAncestryId] = useState("none");
  const [hbClassId, setHbClassId] = useState("none");

  const setAbilityByMod = (key, modStr) => {
    if (modStr === "" || modStr === "-") return;
    const mod = parseInt(modStr, 10);
    if (Number.isNaN(mod)) return;
    setAbilities((prev) => ({ ...prev, [key]: 10 + mod * 2 }));
  };

  const applyOcrDraft = (draft) => {
    setName(draft.name || "");
    setLevel(draft.level || 1);
    setAbilities(draft.abilities);
    if (isDnd5e) {
      setDnd({
        race: draft.dnd5e?.race || "",
        class_name: draft.dnd5e?.class_name || "",
        background: draft.dnd5e?.background || "",
        alignment: draft.dnd5e?.alignment || "",
      });
      setOcrExtra({
        skill_proficiencies: draft.skill_proficiencies,
        hp_current: draft.hp_current,
        hp_max: draft.hp_max,
        currency: draft.currency,
        dnd5e: draft.dnd5e,
      });
    } else {
      if (!isHomebrew) {
        setAncestry(draft.ancestry_slug || "");
        setBackground(draft.background_slug || "");
        setKlass(draft.class_slug || "");
      }
      setOcrExtra({
        skill_proficiencies: draft.skill_proficiencies,
        hp_current: draft.hp_current,
        hp_max: draft.hp_max,
        currency: draft.currency,
        languages: draft.languages,
        heritage_text: draft.heritage_text,
        size: draft.size,
        inventory: draft.inventory,
        known_spell_slugs: draft.known_spell_slugs,
        selected_feat_slugs: draft.selected_feat_slugs,
        custom_features: draft.custom_features,
      });
    }
    toast.success("Photo imported — review the fields, then create your character");
  };

  useEffect(() => {
    if (!open || isHomebrew || isDnd5e) return;
    api.get("/pf2e/ancestries").then((r) => setAncestries(r.data));
    api.get("/pf2e/backgrounds").then((r) => setBackgrounds(r.data));
    api.get("/pf2e/classes").then((r) => setClasses(r.data));
  }, [open, isHomebrew, isDnd5e]);

  useEffect(() => {
    if (!open || !isHomebrew) return;
    api.get(`/games/${gameId}/homebrew/ancestries`).then((r) => setHbAncestries(r.data));
    api.get(`/games/${gameId}/homebrew/classes`).then((r) => setHbClasses(r.data));
  }, [open, isHomebrew, gameId]);

  const create = async () => {
    if (!name.trim()) return toast.error("Give your character a name");
    setSaving(true);
    try {
      let payload;
      if (isHomebrew) {
        const homebrew_values = {};
        for (const f of ruleset?.custom_fields || []) homebrew_values[f.id] = f.type === "checkbox" ? false : f.type === "number" ? 0 : "";
        payload = {
          game_id: gameId, mode: "homebrew", name, abilities, homebrew_values, boost_history: boostHistory,
          homebrew_ancestry_id: hbAncestryId === "none" ? null : hbAncestryId,
          homebrew_class_id: hbClassId === "none" ? null : hbClassId,
        };
      } else if (isDnd5e) {
        payload = { game_id: gameId, mode: "dnd5e", name, level, abilities, dnd5e: dnd, boost_history: boostHistory };
      } else {
        payload =
          mode === "full"
            ? {
                game_id: gameId, mode, name, level, abilities, boost_history: boostHistory,
                ancestry_slug: ancestry === "custom" ? null : ancestry || null,
                background_slug: background === "custom" ? null : background || null,
                class_slug: klass === "custom" ? null : klass || null,
                custom_ancestry_name: ancestry === "custom" ? customAncestryName : null,
                custom_background_name: background === "custom" ? customBackgroundName : null,
                custom_class_name: klass === "custom" ? customClassName : null,
              }
            : { game_id: gameId, mode, name };
      }
      if (ocrExtra) {
        payload = { ...payload, ...ocrExtra };
        // dnd5e is nested: merge the imported extras (AC, attacks, features…) with the current
        // `dnd` state so any post-import edits to Race/Class/Background/Alignment above still win.
        if (isDnd5e && ocrExtra.dnd5e) payload.dnd5e = { ...ocrExtra.dnd5e, ...dnd };
      }
      const { data } = await api.post("/characters", payload);
      onCreated(data);
      onOpenChange(false);
    } catch (err) {
      toast.error("Could not create character");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#322216] border-white/10 text-[#f1e0c5] max-w-lg max-h-[85vh] overflow-y-auto scrollbar-thin" data-testid="character-creator-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Create Your Character</DialogTitle>
          <DialogDescription>
            {isHomebrew
              ? "This table uses a Homebrew ruleset — fill in your ability scores to get started."
              : isDnd5e
              ? "This table uses D&D 5e — fill in your identity and ability scores; modifiers and proficiency bonus are calculated for you."
              : "Choose Full Sheet for auto-calculated PF2e stats, or Paper Character for a quick manual roller."}
          </DialogDescription>
        </DialogHeader>
        <button
          data-testid="open-ocr-import-button"
          onClick={() => setOcrOpen(true)}
          className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#f1e0c5]/40 hover:text-[#D4AF37] border border-white/10 rounded-full px-3 py-1.5 self-start w-fit"
        >
          <ScanLine className="w-3 h-3" /> Or Import a Photo of a Paper Sheet
        </button>
        <OcrImportModal open={ocrOpen} onOpenChange={setOcrOpen} gameId={gameId} isHomebrew={isHomebrew} isDnd5e={isDnd5e} onConfirm={applyOcrDraft} />
        {!isHomebrew && !isDnd5e && (
          <Tabs value={mode} onValueChange={setMode} className="w-full">
            <TabsList className="grid grid-cols-2 bg-black/30">
              <TabsTrigger data-testid="mode-tab-full" value="full">Full Sheet</TabsTrigger>
              <TabsTrigger data-testid="mode-tab-paper" value="paper">Paper Character</TabsTrigger>
            </TabsList>
          </Tabs>
        )}

        <div className="space-y-4 pt-2">
          <Input data-testid="character-name-input" placeholder="Character name" value={name} onChange={(e) => setName(e.target.value)} className="bg-black/30 border-white/10" />

          {isDnd5e && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Input data-testid="dnd-race-input" placeholder="Race" value={dnd.race} onChange={(e) => setDnd({ ...dnd, race: e.target.value })} className="bg-black/30 border-white/10" />
                <Input data-testid="dnd-class-input" placeholder="Class" value={dnd.class_name} onChange={(e) => setDnd({ ...dnd, class_name: e.target.value })} className="bg-black/30 border-white/10" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input data-testid="dnd-background-input" placeholder="Background" value={dnd.background} onChange={(e) => setDnd({ ...dnd, background: e.target.value })} className="bg-black/30 border-white/10" />
                <Input data-testid="dnd-alignment-input" placeholder="Alignment" value={dnd.alignment} onChange={(e) => setDnd({ ...dnd, alignment: e.target.value })} className="bg-black/30 border-white/10" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-[#f1e0c5]/50 mb-2">Level</p>
                <Input data-testid="character-level-input" type="number" min={1} max={20} value={level} onChange={(e) => setLevel(parseInt(e.target.value, 10) || 1)} className="bg-black/30 border-white/10 w-24" />
              </div>
            </>
          )}

          {(isDnd5e || (isHomebrew && ruleset?.rule_modules?.ability_scores)) && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs uppercase tracking-wider text-[#f1e0c5]/50">Ability Scores</p>
                {isDnd5e && (
                  <button data-testid="open-dnd5e-ability-wizard-button" onClick={() => setDnd5eWizardOpen(true)} className="text-[10px] uppercase tracking-wider text-[#D4AF37] hover:underline">Use Ability Wizard</button>
                )}
                {isHomebrew && (
                  <button data-testid="open-homebrew-ability-wizard-button" onClick={() => setPf2eWizardOpen(true)} className="text-[10px] uppercase tracking-wider text-[#D4AF37] hover:underline">Use Ability Wizard</button>
                )}
              </div>
              {isHomebrew && (hbAncestries.length > 0 || hbClasses.length > 0) && (
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <Select value={hbAncestryId} onValueChange={setHbAncestryId}>
                      <SelectTrigger data-testid="homebrew-ancestry-select-creator" className="bg-black/30 border-white/10 text-xs"><SelectValue placeholder="Ancestry (optional)" /></SelectTrigger>
                      <SelectContent><SelectItem value="none">— None / Custom —</SelectItem>{hbAncestries.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Select value={hbClassId} onValueChange={setHbClassId}>
                      <SelectTrigger data-testid="homebrew-class-select-creator" className="bg-black/30 border-white/10 text-xs"><SelectValue placeholder="Class (optional)" /></SelectTrigger>
                      <SelectContent><SelectItem value="none">— None / Custom —</SelectItem>{hbClasses.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-3 gap-3">
                {ABILITIES.map((a) => (
                  <div key={a}>
                    <label className="text-[10px] uppercase text-[#D4AF37]">{a}</label>
                    <div className="flex items-center gap-1.5">
                      <Input
                        data-testid={`character-ability-${a}`}
                        type="number"
                        min={6}
                        max={20}
                        value={abilities[a]}
                        onChange={(e) => setAbilities({ ...abilities, [a]: parseInt(e.target.value, 10) || 10 })}
                        className="bg-black/30 border-white/10"
                      />
                      <Input
                        data-testid={`character-ability-mod-${a}`}
                        type="number"
                        title="Modifier — edit this to set the score directly"
                        value={abilityMod(abilities[a])}
                        onChange={(e) => setAbilityByMod(a, e.target.value)}
                        className="bg-black/20 border-white/10 w-12 shrink-0 text-center text-xs text-[#D4AF37]"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {isHomebrew && (
            <AbilityBoostWizardPf2e
              open={pf2eWizardOpen}
              onOpenChange={setPf2eWizardOpen}
              ancestry={hbAncestries.find((a) => a.id === hbAncestryId) || null}
              background={null}
              klass={hbClasses.find((c) => c.id === hbClassId) || null}
              onComplete={(result, history) => { setAbilities(result); setBoostHistory(history || []); toast.success("Ability scores applied"); }}
            />
          )}
          {isDnd5e && (
            <AbilityScoreWizardDnd5e
              open={dnd5eWizardOpen}
              onOpenChange={setDnd5eWizardOpen}
              use2024Rules={!!game?.variant_rules?.use_2024_ruleset}
              onComplete={({ abilities: result, raceName, backgroundName, boostHistory: history }) => {
                setAbilities(result);
                setDnd((prev) => ({ ...prev, race: raceName || prev.race, background: backgroundName || prev.background }));
                setBoostHistory(history || []);
                toast.success("Ability scores applied");
              }}
            />
          )}

          {!isHomebrew && !isDnd5e && mode === "full" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Select value={ancestry} onValueChange={setAncestry}>
                    <SelectTrigger data-testid="character-ancestry-select" className="bg-black/30 border-white/10"><SelectValue placeholder="Ancestry" /></SelectTrigger>
                    <SelectContent><SelectItem value="custom">— Custom —</SelectItem>{ancestries.map((a) => <SelectItem key={a.slug} value={a.slug}>{a.name}</SelectItem>)}</SelectContent>
                  </Select>
                  {ancestry === "custom" && (
                    <Input data-testid="character-custom-ancestry-input" placeholder="Custom ancestry name" value={customAncestryName} onChange={(e) => setCustomAncestryName(e.target.value)} className="bg-black/30 border-white/10 mt-2" />
                  )}
                </div>
                <div>
                  <Select value={klass} onValueChange={setKlass}>
                    <SelectTrigger data-testid="character-class-select" className="bg-black/30 border-white/10"><SelectValue placeholder="Class" /></SelectTrigger>
                    <SelectContent><SelectItem value="custom">— Custom —</SelectItem>{classes.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                  {klass === "custom" && (
                    <Input data-testid="character-custom-class-input" placeholder="Custom class name" value={customClassName} onChange={(e) => setCustomClassName(e.target.value)} className="bg-black/30 border-white/10 mt-2" />
                  )}
                </div>
              </div>
              <div>
                <Select value={background} onValueChange={setBackground}>
                  <SelectTrigger data-testid="character-background-select" className="bg-black/30 border-white/10"><SelectValue placeholder="Background" /></SelectTrigger>
                  <SelectContent><SelectItem value="custom">— Custom —</SelectItem>{backgrounds.map((b) => <SelectItem key={b.slug} value={b.slug}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
                {background === "custom" && (
                  <Input data-testid="character-custom-background-input" placeholder="Custom background name" value={customBackgroundName} onChange={(e) => setCustomBackgroundName(e.target.value)} className="bg-black/30 border-white/10 mt-2" />
                )}
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-[#f1e0c5]/50 mb-2">Level</p>
                <Input data-testid="character-level-input" type="number" min={1} max={20} value={level} onChange={(e) => setLevel(parseInt(e.target.value, 10) || 1)} className="bg-black/30 border-white/10 w-24" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs uppercase tracking-wider text-[#f1e0c5]/50">Ability Scores</p>
                  <button data-testid="open-pf2e-ability-wizard-button" onClick={() => setPf2eWizardOpen(true)} className="text-[10px] uppercase tracking-wider text-[#D4AF37] hover:underline">Use Ability Boost Wizard</button>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {ABILITIES.map((a) => (
                    <div key={a}>
                      <label className="text-[10px] uppercase text-[#D4AF37]">{a}</label>
                      <div className="flex items-center gap-1.5">
                        <Input
                          data-testid={`character-ability-${a}`}
                          type="number"
                          min={6}
                          max={20}
                          value={abilities[a]}
                          onChange={(e) => setAbilities({ ...abilities, [a]: parseInt(e.target.value, 10) || 10 })}
                          className="bg-black/30 border-white/10"
                        />
                        <Input
                          data-testid={`character-ability-mod-${a}`}
                          type="number"
                          title="Modifier — edit this to set the score directly"
                          value={abilityMod(abilities[a])}
                          onChange={(e) => setAbilityByMod(a, e.target.value)}
                          className="bg-black/20 border-white/10 w-12 shrink-0 text-center text-xs text-[#D4AF37]"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <AbilityBoostWizardPf2e
                open={pf2eWizardOpen}
                onOpenChange={setPf2eWizardOpen}
                ancestry={ancestry && ancestry !== "custom" ? ancestries.find((a) => a.slug === ancestry) : null}
                background={background && background !== "custom" ? backgrounds.find((b) => b.slug === background) : null}
                klass={klass && klass !== "custom" ? classes.find((c) => c.slug === klass) : null}
                onComplete={(result, history) => { setAbilities(result); setBoostHistory(history || []); toast.success("Ability scores applied"); }}
              />
            </>
          )}

          <Button data-testid="character-create-submit" onClick={create} disabled={saving} className="w-full rounded-full bg-[#D90429] hover:bg-[#b8031f] uppercase tracking-widest text-sm">
            {saving ? "Creating…" : "Create Character"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
